import config from 'config';

const getCacheName = (courseSlug) => {
  return `vuo-reader-${window.location.origin}-${courseSlug}`;
};

const getCommonCacheName = () => {
  return `vuo-reader-${window.location.origin}`;
};

// fetch course data and add to cache
const addCourseApiDataToCache = async (cache, courseApiRoute, credentials) => {
  // we need to use fetch to pass credentials
  const response = await fetch(courseApiRoute, { credentials });
  // throw error if reponse is not ok
  if (!response.ok) throw new Error('course api response is not ok');
  // add response to cache
  await cache.put(courseApiRoute, response);

  // also add completion data to cache
  const completionRoute = `${courseApiRoute}/completion`;
  const completionResponse = await fetch(completionRoute, { credentials });
  // throw error if reponse is not ok
  if (!completionResponse.ok)
    throw new Error('course completion api response is not ok');
  // add response to cache
  await cache.put(completionRoute, completionResponse);

  // also add progress data to cache
  const progressRoute = `${courseApiRoute}/progress`;
  const progressResponse = await fetch(progressRoute, { credentials });
  // throw error if reponse is not ok
  if (!progressResponse.ok)
    throw new Error('course progress api response is not ok');
  // add response to cache
  await cache.put(progressRoute, progressResponse);
};

// find image blocks and return an array containing their file urls
const addImageUrlsToCache = (cache, blocks) => {
  return Object.keys(blocks || {})
    .filter((key) => {
      const block = blocks[key];
      return block.type === 'image' && block.data.file && block.data.file.url;
    })
    .map((key) => {
      return blocks[key].data.file.url;
    })
    .forEach((url) => {
      // we need to use fetch to handle cors
      return fetch(url, { mode: 'no-cors' }).then((response) =>
        cache.put(url, response)
      );
    });
};

// put a single chapter api response to cache and return the response
const putChapterApiResponseToCache = (cache, route, response) => {
  // we want to return the response, therefore it must be cloned,
  // because a response can only be consumed once
  const clonedResponse = response.clone();
  return cache.put(route, response).then(() => clonedResponse);
};

// add chapter api routes and files (image, audio) to cache
const addChapterApiDataToCache = (cache, chapterApiRoutes, credentials) => {
  return Promise.all(
    chapterApiRoutes.map(async (route) => {
      // we need to use fetch to pass credentials
      const response = await fetch(route, { credentials: credentials });
      // throw error if reponse is not ok
      if (!response.ok) throw new Error('chapter api response is not ok');

      // handle response and add content to cache
      return putChapterApiResponseToCache(cache, route, response)
        .then((response) => response.json())
        .then((json) => addImageUrlsToCache(cache, json.blocks));
    })
  );
};

const addServerConfigApiDataToCache = async (
  cache,
  serverConfigRoute,
  credentials
) => {
  const response = await fetch(serverConfigRoute, { credentials: credentials });
  if (!response.ok) throw new Error('server config api response is not ok');
  await cache.put(serverConfigRoute, response);
};

const addChapterRoutesToCache = (cache, chapterRoutes, credentials) => {
  return Promise.all(
    chapterRoutes.map(async (route) => {
      const response = await fetch(route, { credentials: credentials });
      if (!response.ok) throw new Error('chapter routeresponse is not ok');
      return cache.put(route, response);
    })
  );
};

const addStaticRoutesToCache = async () => {
  const staticRoutes = [
    '/favicon.ico',
    '/static/flag-icon-css/flags/4x3/de.svg',
    '/static/flag-icon-css/flags/4x3/et.svg',
    '/static/flag-icon-css/flags/4x3/es.svg',
    '/static/flag-icon-css/flags/4x3/fi.svg',
    '/static/flag-icon-css/flags/4x3/fr.svg',
    '/static/flag-icon-css/flags/4x3/en.svg',
    '/static/flag-icon-css/flags/4x3/no.svg',
    '/static/flag-icon-css/flags/4x3/pl.svg',
    '/static/flag-icon-css/flags/4x3/ru.svg',
    '/static/flag-icon-css/flags/4x3/sv.svg',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-600.woff2',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-600italic.woff2',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-700.woff2',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-700italic.woff2',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-regular.woff2',
    '/static/fonts/source-sans-pro/source-sans-pro-v14-latin-ext_latin_cyrillic-italic.woff2',
    '/static/fonts/source-serif-pro/source-serif-pro-v11-latin-ext_latin_cyrillic-600.woff2',
    '/static/fonts/source-serif-pro/source-serif-pro-v11-latin-ext_latin_cyrillic-700.woff2',
    '/static/fonts/source-serif-pro/source-serif-pro-v11-latin-ext_latin_cyrillic-regular.woff2',
    '/static/img/vuo-logo-sininen.svg'
  ];
  const cacheName = getCommonCacheName();
  const cache = await caches.open(cacheName);
  await Promise.all(
    staticRoutes.map(async (route) => {
      const response = await fetch(route);
      if (!response.ok) throw new Error('static file response is not ok');
      await cache.put(route, response);
    })
  );
};

// save course contents offline
const saveCourseData = async (chapterSlugs, courseSlug) => {
  if (!window.caches) {
    return Promise.reject('cache api is not supported');
  }

  // try to aquire persistent storage
  if (
    navigator.storage &&
    navigator.storage.persist &&
    navigator.storage.persisted
  ) {
    navigator.storage.persisted().then((persisted) => {
      if (!persisted) navigator.storage.persist();
    });
  }

  // get cache name
  const cacheName = getCacheName(courseSlug);

  const { apiUrl } = config;

  const credentials = apiUrl.length ? 'include' : 'same-origin';

  // build routes
  const courseApiRoute = `${apiUrl}/reader/courses/${courseSlug}`;
  const chapterApiRoutes = chapterSlugs.map((slug) => {
    return `${apiUrl}/reader/courses/${courseSlug}/chapters/${slug}`;
  });
  const serverConfigRoute = `${apiUrl}/admin/server-config`;
  const chapterRoutes = chapterSlugs.map((slug) => {
    return `/courses/${courseSlug}/${slug}`;
  });

  // open cache and add data
  const cache = await caches.open(cacheName);
  await addCourseApiDataToCache(cache, courseApiRoute, credentials);
  await addChapterApiDataToCache(cache, chapterApiRoutes, credentials);
  await addServerConfigApiDataToCache(cache, serverConfigRoute, credentials);
  await addChapterRoutesToCache(cache, chapterRoutes, credentials);
  await addStaticRoutesToCache();
};

// remove course contents from cache
const removeCourseData = (courseSlug) => {
  if (!window.caches) {
    return Promise.reject('cache api is not supported');
  }

  // get cache name
  const cacheName = getCacheName(courseSlug);

  // remove data from cache storage
  return caches.delete(cacheName);
};

// check if course is saved offline
const getCacheStatus = (courseSlug) => {
  if (!window.caches) {
    return Promise.reject('cache api is not supported');
  }

  // get cache name
  const cacheName = getCacheName(courseSlug);

  // check if cache exists
  return caches.has(cacheName);
};

export default {
  getCacheStatus,
  removeCourseData,
  saveCourseData
};
